import React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { Field, Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { Loading } from '@common/react/components/UI/Loading/Loading';

import { rest } from '@app/components/Api';
import { zipCodeValidator } from '@app/components/LocalCommon/Utils/Validators';

const captchaKey = '6LezTMIcAAAAACaXvLNse5bmTJpwqs0Ezm_bhTrU';

const links: Array<{ h4: string; p: string; btn: string; link: string }> = [
	{
		h4: 'Request Brochures',
		p: 'Order FREE Lap of Love brochures or business cards for distribution at your clinic.',
		btn: 'Order Brochures',
		link: 'https://info.lapoflove.com/clinic-resources#brochures',
	},
	{
		h4: 'Your Service Area',
		p: 'Locate areas with Lap of Love veterinarians so you can guide families to local services available to them.',
		btn: 'Lap of Love Locations',
		link: 'https://www.lapoflove.com/find-a-vet',
	},
	{
		h4: 'Frequently Asked Questions',
		p: 'Learn about the many ways Lap of Love can assist your practice.',
		btn: 'View FAQs',
		link: 'https://info.lapoflove.com/clinic-faq',
	},
];

interface ButtonSubmitProps {
	loading: boolean;
	error: string;
	formikBag: FormikProps<any>;
}

interface SectionFormsProps {
	loginRef: any;
	signupRef: any;
}

interface FormResult {
	redirectUri: string;
}

const signUpValidationSchema = Yup.object({
	firstName: Yup.string().required('First Name is a required field'),
	lastName: Yup.string().required('Last Name is a required field'),
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	zip: zipCodeValidator(),
	role: Yup.string().required('Role is a required field'),
	captcha: Yup.string().required('Please check the box to submit this form'),
});

const loginValidationSchema = Yup.object({
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	captcha: Yup.string().required('Please check the box to submit this form'),
});

const handleSubmit = (url: string, values: {}, setLoading: (e: boolean) => void, setError: (e: string) => void) => {
	setLoading(true);

	rest.post<FormResult>(url, values)
		.then((response) => {
			if (response.redirectUri) {
				window.location.href = response.redirectUri;
			}
		})
		.catch(() => {
			setError('Service Unavailble. Please try again later.');
			setTimeout(() => setLoading(false), 300);
			setTimeout(() => setError(''), 10000);
		})
		.finally(() => {
			setTimeout(() => setLoading(false), 300);
		});
};

const ButtonSubmit: React.FC<ButtonSubmitProps> = ({ loading, error, formikBag }) => {
	return !loading ? (
		!error.length ? (
			<button
				type="submit"
				className={`btn btn-primary ${Object.entries(formikBag.errors).length !== 0 ? 'disabled' : ''}`}
				disabled={Object.entries(formikBag.errors).length !== 0 ? true : false}
			>
				Submit
			</button>
		) : (
			<div className="error-text">{error}</div>
		)
	) : (
		<Loading />
	);
};

const SectionForms: React.FC<SectionFormsProps> = ({ loginRef, signupRef }) => {
	const [loading, setLoading] = React.useState<boolean>(false);
	const [error, setError] = React.useState<string>('');

	return (
		<section className="forms">
			<div className="forms-wrap">
				<div className="forms-wrap-form forms-wrap-form__login" ref={loginRef}>
					<Formik
						onSubmit={values => handleSubmit('v1/form/login', values, setLoading, setError)}
						initialValues={{ email: '' }}
						validationSchema={loginValidationSchema}
						render={(formikBag) => {
							return (
								<>
									<Form>
										<div className="h3">Veterinary Resource Center Log In</div>
										<FormikField title="Email" fieldName="email" />
										<FormikField
											containerClassName="captcha"
											fieldName="captcha"
											render={fieldProps => (
												<ReCAPTCHA
													sitekey={captchaKey}
													onChange={(value) => {
														if (value) {
															formikBag.setFieldValue('captcha', value);
														} else {
															formikBag.setFieldValue('captcha', '');
														}
													}}
													onExpired={() => formikBag.setFieldValue('captcha', 'expired')}
													hl="en"
												/>
											)}
										/>
										<ButtonSubmit loading={loading} error={error} formikBag={formikBag}/>
									</Form>
								</>
							);
						}}
					/>
				</div>
				<div className="forms-wrap-form forms-wrap-form__signup" ref={signupRef}>
					<Formik
						onSubmit={values => handleSubmit('v1/form/register', values, setLoading, setError)}
						initialValues={{ firstName: '', lastName: '', email: '', zip: '', role: '' }}
						validationSchema={signUpValidationSchema}
						render={(formikBag) => {
							return (
								<>
									<Form>
										<div className="h3">
											Veterinary Resource Center
											<br />
											New User Sign-Up
										</div>
										<div className="row-2">
											<FormikField title="First name" fieldName="firstName" />
											<FormikField title="Last name" fieldName="lastName" />
										</div>
										<div className="row-2">
											<FormikField title="Email" fieldName="email" />
											<FormikField title="Postal code" fieldName="zip" />
										</div>
										<FormikField
											title="Your current role"
											fieldName="role"
											render={() => (
												<div className="radio-block">
													<label>
														<Field type="radio" name="role" value="V" />
														Veterinarian
													</label>
													<label>
														<Field type="radio" name="role" value="VS" />
														Veterinary Student
													</label>
													<label>
														<Field type="radio" name="role" value="VT" />
														Veterinary Technician
													</label>
													<label>
														<Field type="radio" name="role" value="Other Veterinary Professional" />
														Other Veterinary Professional
													</label>
												</div>
											)}
										/>
										<div className="checkbox">
											<input type="checkbox" name="checked" id="checked" />
											<label htmlFor="checked">
												Yes I'd like to subscribe to The Compassion Movement, Lap of Love's quarterly e-newsletter
											</label>
										</div>
										<FormikField
											fieldName="captcha"
											containerClassName="captcha"
											render={(fieldProps) => {
												return (
													<ReCAPTCHA
														sitekey={captchaKey}
														onChange={(value) => {
															if (value) {
																formikBag.setFieldValue('captcha', value);
															} else {
																formikBag.setFieldValue('captcha', '');
															}
														}}
														onExpired={() => formikBag.setFieldValue('captcha', 'expired')}
														hl="en"
													/>
												);
											}}
										/>
										<ButtonSubmit loading={loading} error={error} formikBag={formikBag}/>
									</Form>
								</>
							);
						}}
					/>
				</div>
				<div className="forms-wrap-links">
					{links.map((link, i) => (
						<div key={i} className="forms-wrap-links__link">
							<h4>{link.h4}</h4>
							<p>{link.p}</p>
							<a href={link.link} target="_blank" rel="nofollow noreferrer">
								<button className="btn btn-primary">{link.btn}</button>
							</a>
						</div>
					))}
				</div>
			</div>
		</section>
	);
};

export default SectionForms;
